/**
 * iter728 — Browser-side thermal-logo optimiser.
 *
 * Loads the user's selected image entirely client-side, resizes it to
 * the thermal printer's native pixel width (256 / 384 px), applies
 * Floyd–Steinberg dithering to convert to true 1-bit monochrome, and
 * returns a PNG data-URI ready to be saved straight into
 * `branding.thermal_logo_url`.
 *
 * Why this exists: the previous upload path saved a normal image URL.
 * At print time the POS had to re-fetch that URL via canvas with
 * `crossOrigin='anonymous'` — Safari treats any missing CORS header as
 * a hard failure, even on same-origin URLs, so logos silently never
 * appeared on receipts. Storing the already-optimised PNG as a
 * data-URI sidesteps every network and CORS concern: data URIs are
 * inlined into the payload, no fetch, no taint.
 *
 * Also returns measurements so the Settings UI can show the user
 * exactly what's being saved (original size + pixels → optimised size
 * + pixels).
 */

/** Read a File into an HTMLImageElement (used purely for measurement). */
function _loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const cleanup = () => URL.revokeObjectURL(url);
    img.onload = () => { cleanup(); resolve(img); };
    img.onerror = () => { cleanup(); reject(new Error('Could not decode image')); };
    img.src = url;
  });
}

/** Floyd–Steinberg dither + 1-bit PNG. Mirrors the algorithm in
 * `eposPrint.js:_renderLogoEposImage` so what the user sees in the
 * preview is bit-for-bit what the printer will eject. */
function _resizeAndDither(img, targetW) {
  // iter767 — Aspect-preserving cap raised to 240 dots tall (width 480 for
  // 80mm) because users reported the logo printing "very narrow". Mirrors
  // eposPrint.js:_renderLogoEposImage so the saved logo matches the print.
  const MAX_LOGO_H_DOTS = 240;
  const rawRatio = img.naturalHeight / Math.max(1, img.naturalWidth);
  let w = targetW;
  let h = Math.max(1, Math.round(targetW * rawRatio));
  if (h > MAX_LOGO_H_DOTS) {
    const scale = MAX_LOGO_H_DOTS / h;
    w = Math.max(1, Math.round(targetW * scale));
    h = MAX_LOGO_H_DOTS;
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // White background so logos with transparency stay legible.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h);
  const d = px.data;

  // Grayscale buffer for dithering.
  const gray = new Float32Array(w * h);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    gray[j] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      const old = gray[idx];
      const newPx = old < 128 ? 0 : 255;
      const err = old - newPx;
      const off = idx * 4;
      d[off] = newPx; d[off + 1] = newPx; d[off + 2] = newPx; d[off + 3] = 255;
      if (x + 1 < w)              gray[idx + 1]     += err * 7 / 16;
      if (y + 1 < h && x > 0)     gray[idx + w - 1] += err * 3 / 16;
      if (y + 1 < h)              gray[idx + w]     += err * 5 / 16;
      if (y + 1 < h && x + 1 < w) gray[idx + w + 1] += err * 1 / 16;
    }
  }
  ctx.putImageData(px, 0, 0);
  return { dataUrl: canvas.toDataURL('image/png'), width: w, height: h };
}

/** Length of a data URI's base64 payload, in bytes after decode. */
function _bytesFromDataUrl(dataUrl) {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const b64 = dataUrl.slice(comma + 1);
  // 3 bytes per 4 base64 chars (minus padding).
  const padding = (b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0));
  return Math.floor(b64.length * 3 / 4) - padding;
}

/**
 * Public entry-point.
 *
 * @param {File}   file       The image file the user picked.
 * @param {number} paperMm    58 or 80 (mm). Controls target pixel width.
 * @returns {Promise<{
 *   dataUrl: string,         // PNG data URI ready to persist
 *   originalWidth: number,
 *   originalHeight: number,
 *   originalSizeBytes: number,
 *   optimisedWidth: number,
 *   optimisedHeight: number,
 *   optimisedSizeBytes: number,
 * }>}
 */
export async function optimiseThermalLogo(file, paperMm) {
  if (!file) throw new Error('No file provided.');
  // Quick sanity check — reject obviously bad inputs early.
  if (file.size > 8 * 1024 * 1024) {
    throw new Error(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Pick something under 8 MB — thermal logos compress to <30 KB anyway.`);
  }
  const img = await _loadImageFromFile(file);
  if (img.naturalWidth < 10 || img.naturalHeight < 10) {
    throw new Error(`Image is too small (${img.naturalWidth}x${img.naturalHeight}px). Minimum size is 10x10px.`);
  }
  const targetW = (paperMm && paperMm <= 58) ? 320 : 480;
  const { dataUrl, width, height } = _resizeAndDither(img, targetW);

  return {
    dataUrl,
    originalWidth: img.naturalWidth,
    originalHeight: img.naturalHeight,
    originalSizeBytes: file.size,
    optimisedWidth: width,
    optimisedHeight: height,
    optimisedSizeBytes: _bytesFromDataUrl(dataUrl),
  };
}

/** Format a byte count into "12 KB" / "1.4 MB" / "324 B". */
export function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * iter915 — Convert any logo URL (http/https/relative or data URI) into
 * an optimised thermal-ready PNG data URI up-front, BEFORE it reaches
 * any printing path. Sidesteps the SecurityError / tainted-canvas
 * failure the Epson ePOS path hits when it tries to `getImageData` on
 * a cross-origin logo whose response is missing CORS headers.
 *
 * @param {string} url        Logo URL to normalise.
 * @param {number} paperMm    58 or 80 (mm). Controls target pixel width.
 * @returns {Promise<string>} Data URI ("data:image/png;base64,…") or ''
 *                            when the URL is empty / unreachable /
 *                            unable to be re-encoded.
 */
export async function logoUrlToDataUri(url, paperMm) {
  if (!url) return '';
  // Already a data URI — nothing to do.
  if (String(url).startsWith('data:')) return url;
  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const blob = await res.blob();
    // Wrap as File so we can reuse optimiseThermalLogo().
    const file = new File([blob], 'thermal-logo.png', { type: blob.type || 'image/png' });
    const { dataUrl } = await optimiseThermalLogo(file, paperMm || 80);
    return dataUrl || '';
  } catch (_e) {
    // Fallback: try raw FileReader → dataURL without optimisation,
    // then let the raster path scale it. NOTE: `no-cors` returns an
    // OPAQUE response whose blob is empty/unreadable — encoding that
    // produced the infamous blank square on receipts. We now fetch
    // normally (an opaque-mode fetch is useless here) and validate the
    // decoded image is a real >=10x10 picture before returning it.
    try {
      const res2 = await fetch(url, { credentials: 'omit' });
      if (!res2.ok) return '';
      const blob2 = await res2.blob();
      if (!blob2 || blob2.size === 0 || (blob2.type && !blob2.type.startsWith('image/'))) return '';
      const dataUrl2 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => resolve('');
        reader.readAsDataURL(blob2);
      });
      if (!dataUrl2.startsWith('data:image/')) return '';
      // Decode + size-guard: reject anything under 10x10px (blank or
      // tracking-pixel-sized images crash/waste the thermal head).
      const ok = await new Promise((resolve) => {
        const probe = new Image();
        probe.onload = () => resolve(probe.naturalWidth >= 10 && probe.naturalHeight >= 10);
        probe.onerror = () => resolve(false);
        probe.src = dataUrl2;
      });
      return ok ? dataUrl2 : '';
    } catch (_e2) {
      return '';
    }
  }
}
